import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { formatPrice, gradeShort } from "@/lib/format";

function toggle(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function FilterRow({ checked, onChange, label, count, testId }) {
  return (
    <label className="group flex cursor-pointer items-center gap-3 py-1.5 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground has-[[data-state=checked]]:text-foreground">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} />
      <span className="flex-1">{label}</span>
      {count !== undefined && <span className="font-mono text-[10.5px] tracking-wide text-muted-foreground/70">{String(count).padStart(2, "0")}</span>}
    </label>
  );
}

export function FilterSidebar({ meta, filters, setFilters, onClear }) {
  if (!meta) return null;
  const { brands = [], categories = [], grades = [] } = meta;
  const triggerCls = "mono-label py-4 text-muted-foreground hover:no-underline hover:text-foreground [&[data-state=open]]:text-foreground";

  return (
    <div className="space-y-2" data-testid="filter-sidebar">
      <Accordion type="multiple" defaultValue={["marca", "categoria", "grado", "prezzo"]} className="w-full">
        <AccordionItem value="marca" className="border-border">
          <AccordionTrigger className={triggerCls}>Marca</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-0.5 pb-2">
              {brands.map((b) => (
                <FilterRow
                  key={b.key}
                  checked={filters.brands.includes(b.key)}
                  onChange={() => setFilters((f) => ({ ...f, brands: toggle(f.brands, b.key) }))}
                  label={b.key}
                  count={b.count}
                  testId={`filter-brand-${b.key.toLowerCase()}`}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="categoria" className="border-border">
          <AccordionTrigger className={triggerCls}>Categoria</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-0.5 pb-2">
              {categories.map((c) => (
                <FilterRow
                  key={c.key}
                  checked={filters.categories.includes(c.key)}
                  onChange={() => setFilters((f) => ({ ...f, categories: toggle(f.categories, c.key) }))}
                  label={c.label}
                  count={c.count}
                  testId={`filter-category-${c.key}`}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="grado" className="border-border">
          <AccordionTrigger className={triggerCls}>Grado di qualità</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-0.5 pb-2">
              {grades.map((g) => (
                <FilterRow
                  key={g.key}
                  checked={filters.grades.includes(g.key)}
                  onChange={() => setFilters((f) => ({ ...f, grades: toggle(f.grades, g.key) }))}
                  label={gradeShort(g.key)}
                  testId={`filter-grade-${g.key.toLowerCase()}`}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="prezzo" className="border-border">
          <AccordionTrigger className={triggerCls}>Fascia di prezzo</AccordionTrigger>
          <AccordionContent>
            <div className="px-1 pb-2 pt-3">
              <Slider
                min={meta.price_min}
                max={meta.price_max}
                step={10}
                value={filters.price}
                onValueChange={(v) => setFilters((f) => ({ ...f, price: v }))}
                className="my-4"
                data-testid="filter-price-slider"
              />
              <div className="flex items-center justify-between font-mono text-[11px] tracking-wide text-muted-foreground">
                <span data-testid="filter-price-min">{formatPrice(filters.price[0])}</span>
                <span data-testid="filter-price-max">{formatPrice(filters.price[1])}</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="pt-3">
        <Button variant="ghost" size="sm" onClick={onClear} className="-ml-3 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground hover:text-foreground" data-testid="clear-all-filters-button">
          Rimuovi tutti i filtri
        </Button>
      </div>
    </div>
  );
}
